import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { catchError, distinctUntilChanged, map, of, switchMap } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../../../core/api/api.config';
import { FrontendAuthor, FrontendBlog, FrontendStore } from '../../../../core/api/api.models';
import { blogReadMinutes } from '../../../../core/api/blog-text';
import { SavingArenaApiService } from '../../../../core/api/saving-arena-api.service';
import { SeoService } from '../../../../core/seo/seo.service';
import { SsrResponseService } from '../../../../core/seo/ssr-response.service';
import { breadcrumbSchemaFor, breadcrumbTrail } from '../../../../core/seo/breadcrumb-trail';
import { webPageSchema } from '../../../../core/seo/structured-data';
import { Breadcrumb } from '../../../../shared/breadcrumb/breadcrumb';
import { BlogPostCard } from '../../../../shared/blog-post-card/blog-post-card';
import { DevFlag } from '../../../../shared/dev-flag/dev-flag';
import { SectionHeading } from '../../../../shared/section-heading/section-heading';
import { BLOG_AUTHORS, BLOG_POSTS } from '../../blog.mock';

const USER_PLACEHOLDER_SVG =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 160 160%22%3E%3Crect width=%22160%22 height=%22160%22 rx=%2232%22 fill=%22%23f4edf8%22/%3E%3Ccircle cx=%2280%22 cy=%2262%22 r=%2230%22 fill=%22%232d123f%22 opacity=%22.82%22/%3E%3Cpath d=%22M32 137c6-29 24-46 48-46s42 17 48 46%22 fill=%22%232d123f%22 opacity=%22.82%22/%3E%3C/svg%3E';
/** Shown when a post has no thumbnail — the shared card always leads with an image. */
const PLACEHOLDER_IMAGE_URL = '/images/blog-placeholder.svg';
/** Two full rows of the 3-per-row grid, so the last row is never left with a single card. */
const LATEST_POSTS_LIMIT = 6;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

interface AuthorDetailView {
  slug: string;
  name: string;
  role: string;
  bio: string;
  shortDescriptionHtml: string;
  bioHtml: string;
  about: string[];
  imageUrl: string;
  articleCount: number;
  storesCovered: number;
  topicsCovered: number;
  researchCadence: string;
  focus: string;
  joined: string;
  location: string;
  expertise: string[];
  socialLinks: { label: string; url: string }[];
  popularTopics: AuthorTopicView[];
  articles: AuthorArticleView[];
  updatedStores: AuthorStoreView[];
}

interface AuthorTopicView {
  label: string;
  routeLink: string[];
}

/** A store tile in the "recently updated" grid. */
interface AuthorStoreView {
  id: string;
  name: string;
  slug: string;
  logoUrl: string;
  updatedLabel: string;
}

/** One figure in the stats band. */
interface AuthorStatView {
  value: string;
  label: string;
}

interface AuthorArticleView {
  id: string | number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  categorySlug: string;
  readTime: string;
  /** The shared blog card leads with a thumbnail, so it is mapped here too. */
  imageUrl: string;
}

@Component({
  selector: 'app-blog-author-detail-page',
  imports: [Breadcrumb, RouterLink, DevFlag, BlogPostCard, SectionHeading],
  templateUrl: './blog-author-detail-page.html',
  styleUrl: './blog-author-detail-page.scss',
})
export class BlogAuthorDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  private readonly destroyRef = inject(DestroyRef);
  private readonly seo = inject(SeoService);
  private readonly ssrResponse = inject(SsrResponseService);

  protected readonly author = signal<AuthorDetailView>(
    this.fallbackAuthorView(this.route.snapshot.paramMap.get('authorSlug') || undefined),
  );
  protected readonly crumbs = computed(() =>
    breadcrumbTrail({ label: 'Experts', path: '/experts' }, { label: this.author().name }),
  );
  protected readonly latestPosts = computed(() => this.author().articles.slice(0, LATEST_POSTS_LIMIT));
  protected readonly topicsCoveredLabel = computed(() => {
    const topicsCovered = this.author().topicsCovered;
    return `${topicsCovered}`;
  });
  protected readonly updatedStores = computed(() => this.author().updatedStores);
  /**
   * The stats band, with unavailable figures dropped rather than printed. A cell reading "N/A"
   * looks like broken data next to real numbers; a zero is a real answer and is kept.
   */
  protected readonly stats = computed<AuthorStatView[]>(() => {
    const author = this.author();
    return [
      { value: `${author.articleCount}`, label: 'Published Articles' },
      { value: `${author.storesCovered}`, label: 'Stores Covered' },
      { value: this.topicsCoveredLabel(), label: 'Topics Covered' },
      { value: author.researchCadence, label: 'Research Updates' },
    ].filter((stat) => stat.value && stat.value !== 'N/A');
  });

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => params.get('authorSlug') || ''),
        distinctUntilChanged(),
        switchMap((slug) =>
          this.api.getAuthor(slug).pipe(
            map((response) => ({
              author: this.toAuthorView(response.data, slug),
              // An author only counts as resolved when the API actually returned one.
              resolved: Boolean(response.data?.name?.trim()),
              slug,
            })),
            catchError((error: unknown) => {
              this.ssrResponse.noteFetchFailure(error);
              return of({ author: this.fallbackAuthorView(slug), resolved: false, slug });
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ author, resolved, slug }) => {
        this.author.set(author);
        this.applyAuthorSeo(author, resolved, slug);
      });
  }

  /**
   * Title / description / canonical / schema for an author profile, applied where the author
   * resolves. An unknown author slug is a real 404 rather than a 200 placeholder profile (spec §7).
   */
  private applyAuthorSeo(author: AuthorDetailView, resolved: boolean, slug: string): void {
    if (!resolved) {
      this.ssrResponse.setNotFound();
      this.seo.apply({
        title: 'Author not found | SavingArena',
        description: 'The author profile you are looking for is not available on SavingArena.',
        noindex: true,
      });
      this.seo.setJsonLd([]);
      return;
    }

    const name = author.name;
    const title = author.role ? `${name}, ${author.role} | SavingArena` : `${name} | SavingArena`;
    const description =
      author.bio || `Guides, coupon research and savings advice from ${name} on SavingArena.`;

    const path = `/experts/${author.slug || slug}`;
    const url = this.seo.absolute(path);
    // The inline placeholder SVG is a data: URI — not a shareable OG image, so it is skipped.
    const image =
      author.imageUrl && author.imageUrl !== USER_PLACEHOLDER_SVG ? author.imageUrl : undefined;

    this.seo.apply({ title, description, canonicalPath: path, image });

    this.seo.setJsonLd([
      webPageSchema({ origin: this.seo.origin, url, name: title, description }),
      breadcrumbSchemaFor(this.crumbs(), (p) => this.seo.absolute(p)),
    ]);
  }

  protected showFallbackImage(event: Event): void {
    const image = event.target as HTMLImageElement | null;

    if (image && image.src !== USER_PLACEHOLDER_SVG) {
      image.src = USER_PLACEHOLDER_SVG;
    }
  }

  private toAuthorView(apiAuthor: FrontendAuthor, slug: string): AuthorDetailView {
    const fallback = this.fallbackAuthorView(slug);
    const name = this.stripHtml(apiAuthor.name || '');
    const role = this.stripHtml(apiAuthor.designation || '');
    const shortDescriptionHtml = apiAuthor.shortDescription?.trim() || '';
    const bioHtml = apiAuthor.bio?.trim() || '';
    const expertise = Array.isArray(apiAuthor.areasOfExpertise)
      ? apiAuthor.areasOfExpertise.filter((item) => item.trim())
      : [];
    const articles = Array.isArray(apiAuthor.articles)
      ? this.toArticleViews(apiAuthor.articles)
      : [];

    return {
      ...fallback,
      slug: apiAuthor.slug || slug,
      name,
      role,
      bio: this.stripHtml(shortDescriptionHtml),
      shortDescriptionHtml,
      bioHtml,
      about: bioHtml ? [this.stripHtml(bioHtml)] : [],
      imageUrl: this.assetUrl(apiAuthor.profileImagePath) || USER_PLACEHOLDER_SVG,
      articleCount: apiAuthor.totalArticles ?? articles.length,
      storesCovered: apiAuthor.totalStores ?? fallback.storesCovered,
      topicsCovered: this.countArticleCategories(apiAuthor.articles),
      researchCadence: fallback.researchCadence || 'N/A',
      focus: this.commaListFromUnknown(apiAuthor['focus']),
      joined: this.formattedDateFromUnknown(apiAuthor['joiningDate']),
      location: this.stringFromUnknown(apiAuthor['location']),
      expertise,
      popularTopics: this.latestArticleCategories(apiAuthor.articles),
      articles,
      updatedStores: this.toStoreViews(apiAuthor.updatedStores),
    };
  }

  private toStoreViews(stores: FrontendStore[] | undefined): AuthorStoreView[] {
    if (!Array.isArray(stores)) {
      return [];
    }

    return stores
      .filter((store) => store.name && store.slug)
      .map((store) => ({
        id: String(store.id ?? store.slug),
        name: store.name ?? '',
        slug: store.slug ?? '',
        logoUrl: this.assetUrl(store.logoImagePath ?? store.thumbnailImagePath),
        updatedLabel: this.relativeUpdatedLabel(store.updatedAt),
      }));
  }

  /**
   * "Updated 3 days ago" style label. Coarse on purpose — the exact minute is noise on a profile
   * page, and an empty string simply drops the line rather than showing a placeholder date.
   */
  private relativeUpdatedLabel(value: string | undefined): string {
    if (!value) {
      return '';
    }

    const updated = new Date(value);
    if (Number.isNaN(updated.getTime())) {
      return '';
    }

    const days = Math.floor((Date.now() - updated.getTime()) / MILLISECONDS_PER_DAY);
    if (days <= 0) {
      return 'Updated today';
    }
    if (days === 1) {
      return 'Updated yesterday';
    }
    if (days < 30) {
      return `Updated ${days} days ago`;
    }

    const months = Math.floor(days / 30);
    return months === 1 ? 'Updated 1 month ago' : `Updated ${months} months ago`;
  }

  private fallbackAuthorView(slug = BLOG_AUTHORS[0].slug): AuthorDetailView {
    const fallback = BLOG_AUTHORS.find((author) => author.slug === slug);

    if (!fallback) {
      return {
        slug,
        name: this.titleFromSlug(slug),
        role: '',
        bio: '',
        shortDescriptionHtml: '',
        bioHtml: '',
        about: [],
        imageUrl: USER_PLACEHOLDER_SVG,
        articleCount: 0,
        storesCovered: 0,
        topicsCovered: 0,
        researchCadence: 'N/A',
        focus: '',
        joined: '',
        location: '',
        expertise: [],
        socialLinks: [],
        popularTopics: [],
        articles: [],
        updatedStores: [],
      };
    }

    return {
      slug: fallback.slug,
      name: fallback.name,
      role: fallback.role,
      bio: fallback.bio,
      shortDescriptionHtml: fallback.bio,
      bioHtml: fallback.about.map((paragraph) => `<p>${paragraph}</p>`).join(''),
      about: fallback.about,
      imageUrl: fallback.imageUrl,
      articleCount: fallback.articleCount,
      storesCovered: fallback.storesCovered,
      topicsCovered: fallback.topicsCovered,
      researchCadence: fallback.researchCadence,
      focus: fallback.focus,
      joined: fallback.joined,
      location: fallback.location,
      expertise: fallback.expertise,
      socialLinks: fallback.socialLinks,
      popularTopics: fallback.popularTopics.map((topic) => ({
        label: topic,
        routeLink: ['/blogs', this.slugify(topic)],
      })),
      articles: BLOG_POSTS.filter((item) => item.authorSlug === fallback.slug),
      // The mock has no store data; the section simply hides until the API answers.
      updatedStores: [],
    };
  }

  private toArticleViews(blogs: FrontendBlog[]): AuthorArticleView[] {
    return blogs.map((blog) => {
      const categoryName = blog.category?.name ?? 'Shopping Tips';
      const categorySlug = blog.category?.slug || this.slugify(categoryName);

      return {
        id: blog.id,
        slug: blog.slug,
        title: blog.name,
        excerpt: blog.shortDescription
          ? this.stripHtml(blog.shortDescription)
          : this.excerptFromContent(blog.content) || blog.name,
        category: categoryName,
        categorySlug,
        readTime: `${blogReadMinutes(blog)} Min Read`,
        imageUrl: this.assetUrl(blog.thumbnailImagePath) || PLACEHOLDER_IMAGE_URL,
      };
    });
  }

  private countArticleCategories(blogs: FrontendBlog[] | undefined): number {
    if (!Array.isArray(blogs)) {
      return 0;
    }

    const categories = new Set<string>();

    for (const blog of blogs) {
      const category = blog.category;
      const categoryKey = category?.slug || category?.id || category?.name;

      if (categoryKey) {
        categories.add(categoryKey);
      }
    }

    return categories.size;
  }

  private latestArticleCategories(blogs: FrontendBlog[] | undefined): AuthorTopicView[] {
    if (!Array.isArray(blogs)) {
      return [];
    }

    const categoryLinks: AuthorTopicView[] = [];
    const seenCategories = new Set<string>();

    for (const blog of [...blogs].sort((a, b) => this.blogDateMs(b) - this.blogDateMs(a))) {
      const category = blog.category;
      const categoryName = category?.name?.trim();

      if (!categoryName) {
        continue;
      }

      const categoryKey = category?.slug || category?.id || categoryName;

      if (seenCategories.has(categoryKey)) {
        continue;
      }

      seenCategories.add(categoryKey);
      categoryLinks.push({
        label: categoryName,
        routeLink: ['/blogs', category?.slug || this.slugify(categoryName)],
      });

      if (categoryLinks.length === 5) {
        break;
      }
    }

    return categoryLinks;
  }

  private blogDateMs(blog: FrontendBlog): number {
    return this.dateMs(blog.updatedAt) || this.dateMs(blog.publishAt);
  }

  private dateMs(value: string | undefined): number {
    if (!value) {
      return 0;
    }

    const dateMs = new Date(value).getTime();

    return Number.isNaN(dateMs) ? 0 : dateMs;
  }

  private commaListFromUnknown(value: unknown): string {
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === 'string' ? this.stripHtml(item) : ''))
        .filter(Boolean)
        .join(', ');
    }

    return this.stringFromUnknown(value);
  }

  private formattedDateFromUnknown(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) {
      return '';
    }

    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    const date = dateOnlyMatch
      ? new Date(Number(dateOnlyMatch[1]), Number(dateOnlyMatch[2]) - 1, Number(dateOnlyMatch[3]))
      : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  }

  private stringFromUnknown(value: unknown): string {
    return typeof value === 'string' ? this.stripHtml(value) : '';
  }

  private excerptFromContent(value?: string): string {
    if (!value) {
      return '';
    }

    const text = this.stripHtml(value);

    if (text.length <= 150) {
      return text;
    }

    return `${text.slice(0, 150).trimEnd()}...`;
  }

  private stripHtml(value: string): string {
    return value
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private assetUrl(path: string | undefined): string {
    if (!path) {
      return '';
    }

    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const assetBaseUrl = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    const assetPath = path.startsWith('/') ? path : `/${path}`;

    return `${assetBaseUrl}${assetPath}`;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  private titleFromSlug(slug: string): string {
    return slug
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}
